/**
 * 7-day rolling-totals tile — 6 KPIs (signups / match-ready / mutual matches / payments, and since
 * 2026-11-02 became match-ready / accepted by both, counted on the day they happened).
 *
 * Backend shape (per buildSparklineTile):
 *   { signups: Row[], match_ready: Row[], mutual: Row[], payments: Row[] }
 * where Row = { d: string, n: string|number } | { date: string, n: number } (payments
 * variant from metrics.fct_funnel_daily).
 *
 * Pre-launch tolerance: payments row is empty if metrics.fct_funnel_daily
 * has no payment_completed data; we render "—" instead of failing.
 */
import { formatNumber } from '@/lib/utils';
import { HelpTooltip } from '@/components/ui/Tooltip';

interface SparkRow {
  d?: string;
  date?: string;
  n: string | number;
}
interface SparklineData {
  signups: SparkRow[];
  match_ready: SparkRow[];
  mutual: SparkRow[];
  payments: SparkRow[];
  /** 2026-11-02: members who BECAME match-ready, by the day it happened (growth.match_ready_daily). */
  match_ready_events?: SparkRow[];
  /** 2026-11-02: matches FIRST accepted by both members, by that day (matches.status_events_daily). */
  accepted?: SparkRow[];
  /** The window of the two event-day series: the 7 complete UTC days that end yesterday. */
  events_window?: { from: string; to: string };
}

/**
 * Who owns the "why do these numbers differ from the Command Center" answer: this tooltip, not the
 * CEO's daily e-mail (spec 2026-11-02-manny-metrics-email-design.md §10a, Q-6).
 */
const EVENT_DAY_HELP =
  'Same definitions and the same 7 complete UTC days (ending yesterday) as the daily metrics ' +
  'e-mail, so the two numbers are equal. Counted on the day it happened, ' +
  'with no tester, deleted, suspended or consent-withdrawn accounts. The Launch Command Center ' +
  'counts those accounts, so it reads higher (docs/marketing/metrics-dictionary.md §7.2).';

function sumRows(rows: SparkRow[]): number {
  return rows.reduce(
    (sum, r) => sum + (typeof r.n === 'number' ? r.n : parseInt(String(r.n), 10) || 0),
    0,
  );
}

function KpiCard({
  label,
  rows,
  help,
  emptyIsZero = false,
}: {
  label: string;
  rows: SparkRow[];
  help?: string;
  /** A LIVE series with no row is a true 0 (nothing happened), as the e-mail prints it. */
  emptyIsZero?: boolean;
}) {
  const total = sumRows(rows);
  const empty = !emptyIsZero && (!rows || rows.length === 0);
  return (
    <div className="bg-card border rounded-lg p-4">
      <div className="text-xs text-muted-foreground uppercase tracking-wide flex items-center gap-1">
        {label}
        {help && <HelpTooltip content={help} />}
      </div>
      <div className="text-2xl font-semibold mt-1">{empty ? '—' : formatNumber(total)}</div>
      {!empty && (
        <div className="text-xs text-muted-foreground mt-1">
          {rows.length} day{rows.length !== 1 ? 's' : ''} of data
        </div>
      )}
    </div>
  );
}

export function SparklineTile({ data }: { data: SparklineData }) {
  return (
    <div className="md:col-span-2 lg:col-span-3">
      <div className="text-xs text-muted-foreground uppercase tracking-wide mb-2">
        Last 7 days
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
        <KpiCard label="Signups" rows={data.signups || []} />
        <KpiCard label="Match-ready" rows={data.match_ready || []} />
        <KpiCard label="Mutual matches" rows={data.mutual || []} />
        <KpiCard label="Payments" rows={data.payments || []} />
        <KpiCard label="Became match-ready (7 full days)" rows={data.match_ready_events || []} help={EVENT_DAY_HELP} emptyIsZero />
        <KpiCard label="Accepted by both (7 full days)" rows={data.accepted || []} help={EVENT_DAY_HELP} emptyIsZero />
      </div>
    </div>
  );
}
